import { getResults } from 'shared/api'
import { POST } from 'shared/util/fetch-methods'
import { CreationToolFrame, CreationToolSourceFile } from '../types'
import getSourceFrames from './get-source-frames'
import { preloadImage } from './preload'

const MAX_FILE_SIZE = 5 // mb
const DOWNSAMPLE_STEP = 0.8

const getCurrentFileSize = (urls: string[]): number => {
    let fileSize = 0

    urls.forEach((url) => {
        const bytes = url.length * 1.37
        fileSize += bytes / (1024 * 1024)
    })

    return fileSize
}

const decreaseFileSize = async (urls: string[]): Promise<string[]> => {
    const promises: Promise<string>[] = urls.map(
        (url) =>
            new Promise((resolve, reject) => {
                const img = document.createElement('img')
                const canvas = document.createElement('canvas')
                const context = canvas.getContext('2d')
                img.onload = () => {
                    canvas.width = Math.round(img.naturalWidth * DOWNSAMPLE_STEP)
                    canvas.height = Math.round(img.naturalHeight * DOWNSAMPLE_STEP)
                    context?.drawImage(img, 0, 0, canvas.width, canvas.height)
                    resolve(canvas.toDataURL('image/jpeg').split(';base64,')[1])
                }
                img.onerror = reject
                img.src = `data:image/jpeg;base64,${url}`
            })
    )

    return Promise.all(promises)
}

const applyMask = async (frame: CreationToolFrame, mask: string): Promise<CreationToolFrame> => {
    const img = await preloadImage(`data:image/jpeg;base64,${mask}`)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const frameContext = frame.canvas.getContext('2d')

    canvas.width = frame.canvas.width
    canvas.height = frame.canvas.height

    if (context && frameContext) {
        context.drawImage(img, 0, 0, canvas.width, canvas.height)

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        const frameImageData = frameContext.getImageData(0, 0, canvas.width, canvas.height)

        for (let i = 0; i < imageData.data.length; i += 4) {
            const r = imageData.data[i]
            const g = imageData.data[i + 1]
            const b = imageData.data[i + 2]
            const average = (r + g + b) / 3

            imageData.data[i] = frameImageData.data[i]
            imageData.data[i + 1] = frameImageData.data[i + 1]
            imageData.data[i + 2] = frameImageData.data[i + 2]
            imageData.data[i + 3] = Math.round(average / 255) * 255
        }

        context.putImageData(imageData, 0, 0)
    }

    return { ...frame, canvas }
}

const getStickerFromSource = async (sourceFile: CreationToolSourceFile): Promise<CreationToolSourceFile> => {
    const sourceFrames = await getSourceFrames({ sourceFile })
    let encodedFrames = sourceFrames.map(({ canvas }) => canvas.toDataURL('image/jpeg').split(';base64,')[1])
    let currentFileSize = getCurrentFileSize(encodedFrames)

    // downsample to fit max file size
    while (currentFileSize > MAX_FILE_SIZE) {
        encodedFrames = await decreaseFileSize(encodedFrames)
        currentFileSize = getCurrentFileSize(encodedFrames)
    }

    const {
        data: { frames = [] },
    } = await getResults('/ajax/gif/remove_background', POST({ frames: encodedFrames }, true))

    const editCanvas: CreationToolFrame[] = []

    for (let i = 0; i < frames.length; i++) {
        const masked = await applyMask(sourceFrames[i], frames[i].mask)
        editCanvas.push(masked)
    }

    if (editCanvas.length) {
        return {
            ...sourceFile,
            frames: editCanvas,
            hasAudio: false,
            preview: editCanvas[0].canvas,
            isTransparent: true,
        }
    }

    return sourceFile
}

export const getStickerFromCanvas = async (
    canvases: CreationToolFrame[],
    version?: string
): Promise<CreationToolFrame[]> => {
    let encodedFrames = canvases.map(({ canvas }) => canvas.toDataURL('image/jpeg').split(';base64,')[1])
    let currentFileSize = getCurrentFileSize(encodedFrames)

    // downsample to fit max file size
    while (currentFileSize > MAX_FILE_SIZE) {
        encodedFrames = await decreaseFileSize(encodedFrames)
        currentFileSize = getCurrentFileSize(encodedFrames)
    }

    const params: {
        frames: string[]
        version?: string
    } = { frames: encodedFrames }

    if (version) {
        params.version = version
    }

    const {
        data: { frames = [] },
    } = await getResults('/ajax/gif/remove_background', POST(params, true))

    const editCanvas: CreationToolFrame[] = []

    for (let i = 0; i < frames.length; i++) {
        const masked = await applyMask(canvases[i], frames[i].mask)
        editCanvas.push(masked)
    }

    return editCanvas
}

export default getStickerFromSource
